import clsx from "clsx";
import { motion } from "motion/react";
import { CollapseButton } from "../button";
import { Divider } from "../divider";
import type { DividerProps } from "../divider/divider";
import { useDisclosure } from "../hooks";
import type { ChildrenProps } from "../types";

/** Props Interface */
interface CollapserProps extends ChildrenProps {
	label: string | undefined;
	controller?: ReturnType<typeof useDisclosure>;
	gap?: "small" | "medium" | "large";
	withDivider?: boolean;
	dividerProps?: DividerProps;
	defaultIsOpen?: boolean;
}

export function Collapser({
	children,
	controller,
	label,
	gap = "large",
	withDivider = true,
	dividerProps = {},
	defaultIsOpen,
}: CollapserProps) {
	/** Hooks */
	const _disclosure = useDisclosure({ defaultIsOpen });
	const disclosure = controller ?? _disclosure;

	/** Render */
	return (
		<div className="flex flex-col gap-2">
			{label !== undefined ? (
				<div
					className="flex flex-row items-center gap-2 -m-2 w-max cursor-pointer"
					onClick={disclosure.onToggle}
					onKeyUp={disclosure.onToggle}
				>
					<CollapseButton
						isOpen={disclosure.isOpen}
						onToggle={disclosure.onToggle}
						size="xxs"
					/>
					<div className="-ml-2 pointer-events-none">
						<p className="font-medium text-color label">{label}</p>
					</div>
				</div>
			) : null}
			<motion.div
				className={clsx(
					!disclosure.isOpen && "overflow-hidden",
					"form-container",
					gap === "small" && "gap-2",
					gap === "medium" && "gap-4",
					gap === "large" && "gap-6",
					label !== undefined && "pt-2",
				)}
				initial={{ height: disclosure.isOpen ? "auto" : 0 }}
				animate={{ height: disclosure.isOpen ? "auto" : 0 }}
			>
				{withDivider && <Divider {...dividerProps} />}
				{children}
				<div className="h-2" />
			</motion.div>
		</div>
	);
}
